import { useEffect, useState } from 'react';
import { Alert, Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import Page from '../../../components/atoms/page';
import { fetchInspections, InspectionStep, updateInspectionProcess } from '../../../api/inspections';

const MAX_STEPS = 14;
const isWeatherConditionStep = (step: InspectionStep) => step.area === 'Weather Condition';

const keepWeatherConditionLast = (inspectionSteps: InspectionStep[]) => [
  ...inspectionSteps.filter((step) => !isWeatherConditionStep(step)),
  ...inspectionSteps.filter(isWeatherConditionStep),
];

const InspectionProcess = () => {
  const { process: encodedProcess } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const organisationId = searchParams.get('organisationId') ?? '';
  const process = encodedProcess ? decodeURIComponent(encodedProcess) : '';
  const { isAuthenticated, isLoading } = useAuth0();
  const [steps, setSteps] = useState<InspectionStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (isLoading || !isAuthenticated) {
      return;
    }

    if (!organisationId || !process) {
      setLoading(false);
      setError(true);
      return;
    }

    const loadProcess = async () => {
      setLoading(true);
      setError(false);
      try {
        const data = await fetchInspections(organisationId);
        const selectedProcess = data?.find((item) => item.process === process);
        if (!selectedProcess) {
          setError(true);
          return;
        }
        setSteps(keepWeatherConditionLast(selectedProcess.steps));
      } catch (loadError) {
        console.error('Error fetching inspection process', loadError);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadProcess();
  }, [organisationId, process, isAuthenticated, isLoading]);

  const updateStep = (index: number, field: keyof InspectionStep, value: string) => {
    setSaved(false);
    setSteps((current) => {
      const updated = current.map((step, stepIndex) => (
        stepIndex === index ? { ...step, [field]: value } : step
      ));
      return field === 'area' && value === 'Weather Condition'
        ? keepWeatherConditionLast(updated)
        : updated;
    });
  };

  const moveStep = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (
      targetIndex < 0 ||
      targetIndex >= steps.length ||
      isWeatherConditionStep(steps[index]) ||
      isWeatherConditionStep(steps[targetIndex])
    ) return;

    setSaved(false);
    setSteps((current) => {
      const reordered = [...current];
      [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
      return reordered;
    });
  };

  const save = async () => {
    setSaving(true);
    setError(false);
    try {
      await updateInspectionProcess(organisationId, process, steps);
      setSaved(true);
    } catch (saveError) {
      console.error('Error saving inspection process', saveError);
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page title={`${process} inspections`}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back to inspections
      </Button>
      {error && <Alert severity="error" sx={{ mb: 2 }}>Unable to load or save this process.</Alert>}
      {loading ? <Box>Loading inspection steps ...</Box> : (
        <Stack spacing={2}>
          {steps.map((step, index) => (
            <Box key={`${process}-step-${index}`} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField label="Area" value={step.area} onChange={(event) => updateStep(index, 'area', event.target.value)} />
              <TextField label="Section" value={step.section} onChange={(event) => updateStep(index, 'section', event.target.value)} />
              <TextField
                label="Description"
                value={step.description}
                onChange={(event) => updateStep(index, 'description', event.target.value)}
                fullWidth
              />
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <IconButton
                  aria-label="Move step up"
                  onClick={() => moveStep(index, -1)}
                  disabled={index === 0 || isWeatherConditionStep(step)}
                  size="small"
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label="Move step down"
                  onClick={() => moveStep(index, 1)}
                  disabled={index === steps.length - 1 || isWeatherConditionStep(step) || isWeatherConditionStep(steps[index + 1])}
                  size="small"
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </Box>
              <IconButton
                aria-label="Delete step"
                onClick={() => {
                  setSaved(false);
                  setSteps((current) => current.filter((_, stepIndex) => stepIndex !== index));
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              startIcon={<AddIcon />}
              disabled={steps.length >= MAX_STEPS}
              onClick={() => setSteps((current) => (
                current.length >= MAX_STEPS
                  ? current
                  : [...current, { area: '', section: '', description: '' }]
              ))}
            >
              Add step
            </Button>
            <Button variant="contained" onClick={save} disabled={saving}>
              {saving ? 'Saving ...' : 'Save changes'}
            </Button>
            {saved && <Typography color="success.main">Saved</Typography>}
          </Box>
        </Stack>
      )}
    </Page>
  );
};

export default InspectionProcess;